import { FC, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import slugify from "slugify";

import { clsxMerge } from "shared/lib/helpers/styles";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

export interface AvatarI extends PropsWithClassNameI {
  src?: string;
  alt?: string;
  placeholderText?: string;
  shouldTrimPlaceholderText?: boolean;
  imageClassName?: string;
  containerClassName?: string;
  showActiveStatus?: boolean;
  isOnline?: boolean;
  onlineStatusIconClassName?: string;
  isLazy?: boolean;

  onClick?: (event: any) => void;
}

/**
 * Avatar with a photo when `src` loads and initials otherwise. A photo that
 * fails to load (expired signed URL, deleted object, network) falls back to
 * the initials instead of the browser's broken-image glyph, and a new `src`
 * gets a fresh attempt — rotated photo links must not leave a rep stuck on
 * initials.
 */
const Avatar: FC<AvatarI> = ({
  src,
  alt = "",
  placeholderText,
  shouldTrimPlaceholderText = true,
  className,
  imageClassName,
  containerClassName,
  showActiveStatus,
  isOnline,
  onlineStatusIconClassName,
  isLazy = false,
  onClick,
}) => {
  const [isImageLoading, setIsImageLoading] = useState(
    isLazy && src && src.length > 0
  );
  const [hasImageFailed, setHasImageFailed] = useState(false);

  useEffect(() => {
    setHasImageFailed(false);
  }, [src]);

  const shouldShowImage = !!src && !hasImageFailed;

  const placeholder = useMemo(() => {
    if (!shouldTrimPlaceholderText) {
      return placeholderText;
    }

    const cleanText = slugify(placeholderText || "");
    const splitPlaceholder = cleanText?.split("-");

    let text = splitPlaceholder?.map((word) => word?.[0])?.join("");

    text =
      (text?.length || 0) > 2
        ? text?.slice(0, 2)
        : (text?.length || 0) < 2
          ? cleanText?.slice(0, 2)
          : text;

    return (
      <div className="animate-fadein" title={alt || undefined}>
        {text && text?.toUpperCase()}
      </div>
    );
  }, [placeholderText, shouldTrimPlaceholderText, alt]);

  useEffect(() => {
    if (!isLazy) {
      return;
    }

    let image: HTMLImageElement;

    if (src) {
      image = new Image();
      image.src = src;

      image.addEventListener("load", () => {
        setIsImageLoading(false);
      });
      // Without this a lazy photo that fails stays "loading" forever; with it
      // the initials take over, same as the eager path below.
      image.addEventListener("error", () => {
        setHasImageFailed(true);
        setIsImageLoading(false);
      });
    }

    return () => {
      if (src && image && isLazy) {
        image.src = "";
      }
    };
  }, [src, isLazy]);

  return (
    <div className={clsxMerge("relative", containerClassName)}>
      <div
        className={clsxMerge(
          "flex h-6 w-6 items-center justify-center overflow-hidden rounded-full text-white",
          {
            "bg-black p-1": !shouldShowImage || (isLazy && isImageLoading),
          },
          { "ae-typography-detail1": !className?.includes("typography") },
          { "cursor-pointer": !!onClick },
          className
        )}
        onClick={onClick}
      >
        {shouldShowImage ? (
          <>
            {isLazy ? (
              <>
                {isImageLoading ? (
                  placeholder
                ) : (
                  <img
                    className={clsxMerge(
                      "animate-fadein h-full w-full object-cover",
                      imageClassName
                    )}
                    src={src}
                    alt={alt}
                    onError={() => setHasImageFailed(true)}
                  />
                )}
              </>
            ) : (
              <img
                className={clsxMerge(
                  "animate-fadein h-full w-full object-cover",
                  imageClassName
                )}
                src={src}
                alt={alt}
                loading="lazy"
                onError={() => setHasImageFailed(true)}
              />
            )}
          </>
        ) : (
          placeholder
        )}
      </div>

      {showActiveStatus && (
        <div
          className={clsx(
            "h-3 w-3 rounded-full",
            "absolute bottom-[-2px] right-[-2px]",
            {
              "bg-[#74DB64]": isOnline,
              "bg-[#D9D9D9]": !isOnline,
            },
            onlineStatusIconClassName
          )}
        />
      )}
    </div>
  );
};

export default Avatar;
